import React from 'react';
import { Avatar, Grid, Typography } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid';
import { Close } from '@mui/icons-material';
import { PrimaryOutlinedButton, PrimaryTable, VimeoThumbnail } from 'components';
import { ClipboardIcon } from 'assets/icons';
import { getDocumentIcon, FileType } from 'utils/file-uploader';
import { isYouTubeUrl, extractYouTubeVideoId, getYouTubeThumbnailUrl } from 'utils/youtube-utils';
import { isVimeoUrl } from 'utils/vimeo-utils';
import { UrlType } from 'pages/media-library/interface';
import '../index.scss';
import { IContentAvatarWithAction } from '../../../../../../content/interface';

interface SelectedContentTableProps {
  items:IContentAvatarWithAction[],
  handelRemove?:(id:string)=>void
  edit?:boolean

}

const SelectedContentTable = ({
  items, handelRemove = (id:string) => {},
  edit,
}:SelectedContentTableProps) => {
  type Row = typeof items[number];

  const getThumbnailUrl = (item: IContentAvatarWithAction) => {
    if (item.fileCategory === FileType.DOCUMENT) {
      return getDocumentIcon(item.fileType);
    }

    if (item.fileCategory === FileType.VIDEO) {
      const hasYouTubeUrlType = (item as any).urlType === UrlType.YOUTUBE_LINK;
      const hasVimeoUrlType = (item as any).urlType === UrlType.VIMEO_LINK;
      const isYouTube = isYouTubeUrl(item.contentUrl);
      const isVimeo = isVimeoUrl(item.contentUrl);

      if (hasYouTubeUrlType || isYouTube) {
        const videoId = extractYouTubeVideoId(item.contentUrl);
        if (videoId) {
          return getYouTubeThumbnailUrl(videoId, 'medium');
        }
      }

      if (hasVimeoUrlType || isVimeo) {
        // For Vimeo, we'll return null and handle async loading in the component
        return null;
      }
    }

    return item.thumbnail;
  };

  const editColumn = React.useMemo<GridColumns<Row>>(
    () => [
      {
        field: 'title',
        headerName: 'Media Title',
        headerClassName: 'selected-item-column',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        renderCell: (params) => (
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Grid container direction="row" alignItems="center">
                <Grid item sx={{ mr: 0.7 }}>
                  {(() => {
                    const hasVimeoUrlType = (params.row as any).urlType === UrlType.VIMEO_LINK;
                    const isVimeo = isVimeoUrl(params.row.contentUrl);

                    if (hasVimeoUrlType || isVimeo) {
                      return (
                        <VimeoThumbnail
                          url={params.row.contentUrl}
                          width={30}
                          height={30}
                          sx={{
                            width: 30,
                            height: 30,
                            mr: 1,
                            backgroundColor: 'background.default',
                            color: 'primary.main',
                            borderWidth: 1,
                            borderColor: 'primary.light',
                            borderStyle: 'solid',
                          }}
                        />
                      );
                    }

                    return (
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          mr: 1,
                          backgroundColor: 'background.default',
                          color: 'primary.main',
                          borderWidth: 1,
                          borderColor: 'primary.light',
                          borderStyle: 'solid',
                        }}
                        src={getThumbnailUrl(params.row) || undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item>
                  <Typography variant="h4" sx={{ fontWeight: '400' }}>{params.value}</Typography>
                </Grid>

              </Grid>
            </Grid>

          </Grid>

        ),
      },
      {
        field: 'fileType',
        headerName: 'File type',
        headerClassName: 'selected-item-column',
        headerAlign: 'left',
        align: 'left',
        flex: 0.5,
      },
      {
        field: 'isAdded',
        headerName: '',
        headerClassName: 'selected-item-column',
        headerAlign: 'left',
        align: 'left',
        flex: 0.5,
        renderCell: (params) => (
          <Grid container alignItems="center" sx={{ color: '#E93E3E' }}>
            <Close />
            <PrimaryOutlinedButton
              onClick={() => {
                handelRemove(params.row?.id ?? params.row._id);
              }}
            >
              Remove
            </PrimaryOutlinedButton>
          </Grid>
        ),
      },

    ],
    [],
  );

  const viewColumn = React.useMemo<GridColumns<Row>>(
    () => [
      {
        field: 'title',
        headerName: 'Media Title',
        headerClassName: 'selected-item-column',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        renderCell: (params) => (
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Grid container direction="row" alignItems="center">
                <Grid item sx={{ mr: 0.7 }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      mr: 1,
                      backgroundColor: 'background.default',
                      color: 'primary.main',
                      borderWidth: 1,
                      borderColor: 'primary.light',
                      borderStyle: 'solid',
                    }}
                  >
                    <ClipboardIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                </Grid>
                <Grid item>
                  <Typography variant="h4">{params.value}</Typography>
                </Grid>

              </Grid>
            </Grid>

          </Grid>

        ),
      },
      {
        field: 'fileType',
        headerName: 'File type',
        headerClassName: 'selected-item-column',
        headerAlign: 'left',
        align: 'left',
        flex: 0.5,
      },

    ],
    [],
  );
  return (
    <PrimaryTable
      className="selected-table"
      disableSelectionOnClick
      rows={items}
      columns={edit ? editColumn : viewColumn}
      loading={false}
      hideFooter
      headerHeight={30}
    />

  );
};
SelectedContentTable.defaultProps = {
  handelRemove: (id:string) => {},
  edit: true,
};

export default SelectedContentTable;
